import { FC, useCallback, useMemo, useState } from "react";
import { BoostingCampaign } from "@vviinn/user-managment-client-ts";
import { FieldsGroupWrapper } from "../../../ui/layout/organisms/SlideOver/SlideOverFixedLayouts/SlideOverFieldsGroups/FieldsGroupWrapper";
import { getFieldsProps } from "../../../ui/layout/organisms/SlideOver/SlideOverFixedLayouts/SlideOverFieldsGroups/helpers/getFieldsProps";
import { RenderFieldProps } from "../../../ui/layout/organisms/SlideOver/SlideOverFixedLayouts/SlideOverFieldsGroups/types";
import {
  boostingNumberProps,
  EMPTY_PREFERRED_VALUE,
  PREFERRED_VALUE_REQUIRED_KEYS,
} from "../constants";
import {
  PreferredValueDraft,
  deserializePreferredValues,
  serializePreferredValues,
} from "../utils";

const pvRenderProps: RenderFieldProps<keyof PreferredValueDraft>[] = [
  { key: "weight", title: "Weight", type: "number", ...boostingNumberProps },
  { key: "rawValue", title: "Value" },
];

export const PreferredValuesFields: FC<{
  boosting: BoostingCampaign | undefined;
  upsertBoosting: (data: Partial<BoostingCampaign>) => Promise<boolean>;
}> = ({ boosting, upsertBoosting }) => {
  const [drafts, setDrafts] = useState<PreferredValueDraft[]>([]);

  const { handleRemove, handleAdd, fieldsRenderData, areSameFields } = useMemo(
    () =>
      getFieldsProps<PreferredValueDraft>(
        setDrafts,
        EMPTY_PREFERRED_VALUE,
        PREFERRED_VALUE_REQUIRED_KEYS,
      ),
    [],
  );

  const initConfig = useCallback(() => {
    setDrafts(deserializePreferredValues(boosting?.preferredValues));
  }, [boosting?.preferredValues]);

  const savePv = useCallback(async () => {
    const preferredValues = serializePreferredValues(drafts);
    return upsertBoosting({
      preferredValues: preferredValues.length ? preferredValues : undefined,
    });
  }, [drafts, upsertBoosting]);

  const renderFields = useMemo(
    () => fieldsRenderData(drafts, pvRenderProps),
    [drafts, fieldsRenderData],
  );

  const allowSubmit = useMemo(
    () =>
      !areSameFields(
        deserializePreferredValues(boosting?.preferredValues),
        drafts,
      ),
    [boosting?.preferredValues, areSameFields, drafts],
  );

  return (
    <FieldsGroupWrapper
      fieldsType="preferredValues"
      initFieldsCount={boosting?.preferredValues?.length ?? 0}
      initConfig={initConfig}
      addFieldHandler={handleAdd}
      removeFieldHandler={handleRemove}
      submitFieldHandler={savePv}
      importantFields={["rawValue"]}
      allowSubmit={allowSubmit}
      fieldRender={renderFields}
    />
  );
};
